import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import * as api from '../lib/api'
import { setOnSessionExpired } from '../lib/api'
import * as analytics from '../lib/analytics'
import { setLocale, SUPPORTED_LOCALES } from '../i18n'
import type { SupportedLocale } from '../lib/api'
import type { User } from '../types'

interface AuthState {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (
    email: string,
    password: string,
    name: string,
    confirmedMinimumAge: boolean,
  ) => Promise<void>
  logout: () => void
  // setSession hydrates an authed state from a user the caller already
  // obtained through another flow (e.g. post-password-reset auto-
  // login). The cookie was set by the backend response; this just
  // syncs the in-memory user shape. The `token` arg is retained for
  // call-site compatibility but ignored (#123 — auth lives in the
  // httpOnly cookie).
  setSession: (token: string, user: User) => void
  // updateUser swaps just the user object — used after a profile edit
  // (e.g. name change) when the token is unchanged.
  updateUser: (user: User) => void
}

// Exported so components that want to render conditionally without
// crashing when AuthProvider is absent (e.g. the EmailVerifyBanner mounted
// inside Shell, which the Shell test renders without auth wiring) can
// useContext directly and bail on null.
export const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const logout = useCallback(() => {
    // Fire-and-forget — server-side revocation is best-effort; always
    // clear local state. The httpOnly cookie is cleared by the
    // /auth/logout response (#123); we just drop the cached user blob
    // we use for fast-paint and reset analytics.
    api.logout().catch(() => {})
    localStorage.removeItem('user')
    // Defensive: prior builds stored the bearer in localStorage. Strip
    // it on logout so an upgrade from the old SPA build doesn't leave
    // a stale token sitting around — purely belt-and-braces, the JWT
    // can no longer be presented as the bearer once the cookie is the
    // only source of truth.
    localStorage.removeItem('token')
    setUser(null)
    // Drop PostHog's identified-user state so subsequent events are
    // recorded under a fresh anonymous distinct_id.
    analytics.reset()
  }, [])

  // Restore session by paint-fast from the cached `user` blob, then
  // confirm against GET /me (which reads the httpOnly session cookie
  // server-side, #123). The local blob is purely a paint-perf
  // optimisation — auth state is the cookie, not anything we put in
  // storage. Two common staleness modes: (1) blob predates a User
  // struct field added since (e.g. email_verified_at), or (2) the user
  // verified / changed something in another browser. On 401 the api
  // client's onSessionExpired hook runs logout for us; other errors
  // are swallowed so a network blip doesn't kick the user out.
  useEffect(() => {
    const stored = localStorage.getItem('user')
    if (stored) {
      const parsed = parseStoredUser(stored)
      if (parsed) {
        setUser(parsed)
        // Wrapped in try/catch because vitest's mock barrier throws on
        // any property access that wasn't declared in vi.mock(...), and
        // many test files stub `* as api` without including getMe /
        // getPreferences. Production keeps the bootstrap refresh + the
        // analytics-consent sync; tests that don't care just skip them.
        try {
          const fn = api.getMe
          if (typeof fn === 'function') {
            fn()
              .then((fresh) => {
                const json = JSON.stringify(fresh)
                const next = parseStoredUser(json)
                if (next) {
                  localStorage.setItem('user', json)
                  setUser(next)
                }
              })
              .catch(() => {
                // Network / non-401 error — keep the cached user.
              })
          }
        } catch {
          // Module-mock barrier in tests — no /me refresh available.
        }
        try {
          if (
            typeof api.getPreferences === 'function' &&
            typeof api.updatePreferences === 'function'
          ) {
            analytics
              .syncConsentFromServer({
                getPreferences: api.getPreferences,
                updatePreferences: api.updatePreferences,
              })
              .catch(() => {
                // sync swallows its own failures, but the optional
                // chain above might throw synchronously in odd mock
                // shapes — this is the belt-and-braces.
              })
            // Locale reconciliation, same shape as the consent sync:
            // server is authoritative for logged-in users. If the
            // server has a value that differs from the client's
            // current language, switch the UI; if the server is at
            // baseline ('en') but localStorage has picked a non-en
            // language pre-login, push that choice up so it follows
            // the account on the next device.
            api
              .getPreferences()
              .then(async (prefs) => {
                const serverLocale = prefs.locale as SupportedLocale
                const localPick = localStorage.getItem('luno_locale') as SupportedLocale | null
                const inSet = (v: string | null): v is SupportedLocale =>
                  v !== null && (SUPPORTED_LOCALES as readonly string[]).includes(v)
                if (serverLocale !== 'en') {
                  // Server wins.
                  await setLocale(serverLocale)
                } else if (inSet(localPick) && localPick !== 'en') {
                  // Push local choice up; keep UI as-is.
                  try {
                    await api.updatePreferences({ locale: localPick })
                  } catch {
                    // Best-effort; the next session will retry.
                  }
                }
              })
              .catch(() => {
                // Network / non-401 error — keep the local language.
              })
          }
        } catch {
          // Module-mock barrier — preference sync unavailable in tests.
        }
      } else {
        // Tampered or shape-invalid blob — drop it and any stale token
        // from the pre-cookie SPA build.
        localStorage.removeItem('user')
        localStorage.removeItem('token')
      }
    }
    setLoading(false)
  }, [])

  // Register the session-expired callback so the API client can trigger logout
  useEffect(() => {
    setOnSessionExpired(logout)
  }, [logout])

  // Keep Sentry's user context in sync with auth state. We only send the
  // opaque user ID — never email or name — per the PII policy in
  // DECISIONS.md 2026-04-23 "Sentry integration".
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id })
    } else {
      Sentry.setUser(null)
    }
  }, [user])

  // Mirror onto PostHog so funnel events from session-restoration
  // visits (returning user, no fresh login this session) still attach
  // to the right distinct_id. login/register also call identify
  // explicitly so the post-signup signup_completed event lands
  // identified rather than anonymous; this effect catches the
  // restored-session case. Same opaque-id-only PII policy as Sentry.
  useEffect(() => {
    if (user) {
      analytics.identify(user.id)
    }
    // Don't reset on user=null here — logout already does it, and a
    // null user during initial auth-restore would erroneously reset a
    // session that hasn't been identified yet.
  }, [user])

  const login = useCallback(async (email: string, password: string) => {
    // The cookie is set by the response (#123); we just cache the
    // user blob for fast paint on next reload. The body's `token`
    // field is still emitted by the backend for one-deploy
    // backward-compat with bearer clients but the SPA ignores it.
    const res = await api.login(email, password)
    localStorage.setItem('user', JSON.stringify(res.user))
    setUser(res.user as User)
    analytics.identify(res.user.id)
  }, [])

  const register = useCallback(
    async (
      email: string,
      password: string,
      name: string,
      confirmedMinimumAge: boolean,
    ) => {
      const res = await api.register(email, password, name, confirmedMinimumAge)
      localStorage.setItem('user', JSON.stringify(res.user))
      setUser(res.user as User)
      // Identify *before* tracking so the signup_completed event lands
      // attributed to the new user rather than the anonymous pre-signup
      // distinct_id. PostHog merges the prior anonymous events into this
      // identity automatically.
      analytics.identify(res.user.id)
      analytics.track('signup_completed')
    },
    [],
  )

  // setSession's `token` arg is retained for caller compatibility but
  // ignored — the backend's response already set the httpOnly cookie
  // alongside whatever body shape the caller is forwarding.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const setSession = useCallback((_token: string, sessionUser: User) => {
    localStorage.setItem('user', JSON.stringify(sessionUser))
    setUser(sessionUser)
    analytics.identify(sessionUser.id)
  }, [])

  const updateUser = useCallback((next: User) => {
    localStorage.setItem('user', JSON.stringify(next))
    setUser(next)
  }, [])

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, logout, setSession, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

// parseStoredUser shape-validates the localStorage 'user' blob before
// rehydrating state. A tampered or legacy-shaped value would otherwise be
// trusted as a User and crash downstream consumers that read user.name etc.
export function parseStoredUser(raw: string): User | null {
  let value: unknown
  try {
    value = JSON.parse(raw)
  } catch {
    return null
  }
  if (!value || typeof value !== 'object') return null
  const v = value as Record<string, unknown>
  if (typeof v.id !== 'string' || v.id === '') return null
  if (typeof v.email !== 'string' || v.email === '') return null
  if (typeof v.name !== 'string') return null
  if (typeof v.created_at !== 'string') return null
  // email_verified_at: optional, must be null or an ISO-shaped string.
  // Anything else from a tampered localStorage gets dropped to undefined,
  // and the banner will treat that as "unverified" until /me confirms.
  let emailVerifiedAt: string | null | undefined
  if (v.email_verified_at === null) {
    emailVerifiedAt = null
  } else if (typeof v.email_verified_at === 'string') {
    emailVerifiedAt = v.email_verified_at
  } else {
    emailVerifiedAt = undefined
  }
  return {
    id: v.id,
    email: v.email,
    name: v.name,
    created_at: v.created_at,
    email_verified_at: emailVerifiedAt,
  }
}
